import { DateTime } from "luxon";
import { V2_REMINDER_FREQUENCIES, V2_WEEKDAY_KEYS } from "./v2Schema";
import { getReminderTimes, normalizeWeekdays } from "./v2ReminderUtils";

export function getBrowserTimezone() {
  if (typeof Intl === "undefined") return "UTC";
  return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
}

export function calculateNextReminderRun(reminder = {}, { now = new Date(), timezone = getBrowserTimezone() } = {}) {
  if (!reminder?.active) return null;

  const zone = normalizeTimezone(timezone);
  const current = DateTime.fromJSDate(now instanceof Date ? now : new Date(now), { zone });
  if (!current.isValid) return null;

  const reminderTimes = getReminderTimes(reminder);
  if (reminderTimes.length === 0) return null;

  const candidates = [];

  if (reminder.frequency === V2_REMINDER_FREQUENCIES.once) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(String(reminder.once_date || ""))) return null;
    reminderTimes.forEach((time) => addCandidate(candidates, reminder.once_date, time, zone, current));
    return firstIsoCandidate(candidates);
  }

  if (reminder.frequency === V2_REMINDER_FREQUENCIES.specificDays) {
    const days = normalizeWeekdays(reminder.specific_days);
    if (days.length === 0) return null;

    for (let offset = 0; offset <= 7; offset += 1) {
      const day = current.plus({ days: offset });
      const weekdayKey = V2_WEEKDAY_KEYS[day.weekday % 7];
      if (!days.includes(weekdayKey)) continue;
      reminderTimes.forEach((time) => addCandidate(candidates, day.toISODate(), time, zone, current));
      const next = firstIsoCandidate(candidates);
      if (next) return next;
    }

    return null;
  }

  if (reminder.frequency === V2_REMINDER_FREQUENCIES.daily) {
    [current, current.plus({ days: 1 })].forEach((day) => {
      reminderTimes.forEach((time) => addCandidate(candidates, day.toISODate(), time, zone, current));
    });
    return firstIsoCandidate(candidates);
  }

  return null;
}

export function calculateNextDailySystemReminderRun(
  { enabled = false, time = "19:30" } = {},
  { now = new Date(), timezone = getBrowserTimezone() } = {}
) {
  if (!enabled || !/^([01]\d|2[0-3]):[0-5]\d$/.test(String(time || ""))) return null;

  const zone = normalizeTimezone(timezone);
  const current = DateTime.fromJSDate(now instanceof Date ? now : new Date(now), { zone });
  if (!current.isValid) return null;

  const [hour, minute] = String(time).split(":").map(Number);
  const today = current.set({ hour, minute, second: 0, millisecond: 0 });
  const next = today.toMillis() >= current.toMillis() ? today : today.plus({ days: 1 });

  return next.toUTC().toISO();
}

export function formatServerReminderTime(value) {
  if (!value) return "No server reminder scheduled";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "No server reminder scheduled";
  return date.toLocaleString([], {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function addCandidate(candidates, dateKey, time, zone, current) {
  const [hour, minute] = String(time || "").split(":").map(Number);
  if (!Number.isInteger(hour) || !Number.isInteger(minute)) return;

  const candidate = DateTime.fromISO(`${dateKey}T${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}:00`, {
    zone,
  });

  if (candidate.isValid && candidate.toMillis() >= current.toMillis()) {
    candidates.push(candidate);
  }
}

function firstIsoCandidate(candidates = []) {
  const next = candidates.sort((a, b) => a.toMillis() - b.toMillis())[0];
  return next ? next.toUTC().toISO() : null;
}

function normalizeTimezone(timezone) {
  const zone = String(timezone || "UTC").trim();
  return DateTime.local().setZone(zone).isValid ? zone : "UTC";
}
